import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";
import {
  producaoService,
  RelatorioSaboresProducaoFilters,
  RelatorioSaboresProducaoItem,
} from "../../../services/producaoService";
import { lojaService } from "../../../services/lojaService";
import { tamanhoService } from "../../../services/tamanhoService";
import { cardapioService } from "../../../services/cardapioService";
import { usuarioService } from "../../../services/usuarioService";
import { BrazilDate } from "../../../utils/BrazilDate";
import { getErrorMessage } from "../../../utils/errorHandler";
import { warnIfReportPeriodInvalid } from "../utils/reportPeriodValidation";
import { useRankingEvents } from "../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../constants/rankingEventQueryKeys";
import {
  buildBottom10SaboresAgregados,
  buildTop10SaboresAgregados,
} from "../utils/relatorioSaboresChartData";

function getDefaultDateRange() {
  const now = new Date();
  const timezone = BrazilDate.getTimezone();
  const yearStr = format(now, "yyyy", { timeZone: timezone });
  const monthStr = format(now, "MM", { timeZone: timezone });
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const firstDay = BrazilDate.create(year, month, 1);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const firstDayNextMonth = BrazilDate.create(nextYear, nextMonth, 1);
  const lastDay = new Date(firstDayNextMonth.getTime() - 24 * 60 * 60 * 1000);
  return {
    dataInicio: BrazilDate.formatISO(firstDay),
    dataFim: BrazilDate.formatISO(lastDay),
  };
}

interface RelatorioSaboresContextValue {
  startDate: string;
  endDate: string;
  setStartDate: (v: string) => void;
  setEndDate: (v: string) => void;
  selectedLojaId: number | null;
  setSelectedLojaId: (v: number | null) => void;
  selectedTamanhoId: number | null;
  setSelectedTamanhoId: (v: number | null) => void;
  selectedSaborIds: number[];
  setSelectedSaborIds: (v: number[]) => void;
  selectedUsuarioId: number | null;
  setSelectedUsuarioId: (v: number | null) => void;
  handleApplyFilter: () => void;
  handleClearOptionalFilters: () => void;
  lojaOptions: Array<{ value: number; label: string }>;
  tamanhoOptions: Array<{ value: number; label: string }>;
  saborOptions: Array<{ value: number; label: string }>;
  usuarioOptions: Array<{ value: number; label: string }>;
  allTamanhoOptions: Array<{ value: number; label: string }>;
  allUsuarioOptions: Array<{ value: number; label: string }>;
  linhas: RelatorioSaboresProducaoItem[];
  isLoading: boolean;
  queryErrorMessage: string | null;
  totalQuantidade: number;
  appliedFilters: RelatorioSaboresProducaoFilters | null;
  top10SaboresNoPeriodo: ReturnType<typeof buildTop10SaboresAgregados>;
  bottom10SaboresNoPeriodo: ReturnType<typeof buildBottom10SaboresAgregados>;
  isLoadingTop10Periodo: boolean;
  top10TamanhoId: number | null;
  setTop10TamanhoId: (v: number | null) => void;
  top10Partes: number | null;
  setTop10Partes: (v: number | null) => void;
}

const RelatorioSaboresContext =
  createContext<RelatorioSaboresContextValue | null>(null);

export const RelatorioSaboresProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const defaults = useMemo(() => getDefaultDateRange(), []);
  const [appliedFilters, setAppliedFilters] =
    useState<RelatorioSaboresProducaoFilters | null>(null);

  const [startDate, setStartDate] = useState(defaults.dataInicio);
  const [endDate, setEndDate] = useState(defaults.dataFim);
  const [selectedLojaId, setSelectedLojaId] = useState<number | null>(null);
  const [selectedTamanhoId, setSelectedTamanhoId] = useState<number | null>(
    null
  );
  const [selectedSaborIds, setSelectedSaborIds] = useState<number[]>([]);
  const [selectedUsuarioId, setSelectedUsuarioId] = useState<number | null>(
    null
  );
  const [top10TamanhoId, setTop10TamanhoId] = useState<number | null>(null);
  const [top10Partes, setTop10Partes] = useState<number | null>(null);

  useEffect(() => {
    setSelectedTamanhoId(null);
  }, [selectedLojaId]);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const { data: tamanhos = [] } = useQuery({
    queryKey: ["tamanhos-relatorio-sabores", selectedLojaId],
    queryFn: () =>
      tamanhoService.getTamanhos(
        selectedLojaId != null ? { lojaId: selectedLojaId } : undefined
      ),
  });

  const { data: allTamanhosForLabels = [] } = useQuery({
    queryKey: ["tamanhos-relatorio-sabores-todos-labels"],
    queryFn: () => tamanhoService.getTamanhos(),
    staleTime: 120_000,
  });

  const { data: allUsuariosResponse } = useQuery({
    queryKey: ["usuarios-relatorio-sabores-todos-labels"],
    queryFn: () => usuarioService.getUsuarios({ limit: 2000, page: 1 }),
    staleTime: 120_000,
  });

  const { data: sabores = [] } = useQuery({
    queryKey: ["cardapio-sabores-relatorio"],
    queryFn: () => cardapioService.getSabores(),
  });

  const { data: usuariosResponse } = useQuery({
    queryKey: ["usuarios-relatorio-sabores", selectedLojaId],
    queryFn: () =>
      usuarioService.getUsuarios({
        limit: 500,
        page: 1,
        ...(selectedLojaId != null ? { lojaId: selectedLojaId } : {}),
      }),
  });

  const usuariosLista = usuariosResponse?.data ?? [];

  useRankingEvents("producao", appliedFilters?.lojaId ?? null, {
    queryKeys: RANKING_SSE_QUERY_KEYS.relatorioSaboresProducao,
  });

  const {
    data: linhas = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["relatorio-sabores-producao", appliedFilters],
    queryFn: () =>
      producaoService.getRelatorioSabores(
        appliedFilters as RelatorioSaboresProducaoFilters
      ),
    enabled: !!appliedFilters?.dataInicio && !!appliedFilters?.dataFim,
  });

  const queryErrorMessage = isError ? getErrorMessage(error) : null;

  const periodoSomente =
    appliedFilters?.dataInicio && appliedFilters?.dataFim
      ? {
          dataInicio: appliedFilters.dataInicio,
          dataFim: appliedFilters.dataFim,
          lojaId: null,
          tamanhoId: top10TamanhoId,
          partes: top10Partes,
          saborIds: undefined,
          usuarioId: null,
        }
      : null;

  const { data: linhasPeriodoCompleto = [], isLoading: isLoadingTop10Periodo } =
    useQuery({
      queryKey: [
        "relatorio-sabores-top10",
        periodoSomente?.dataInicio,
        periodoSomente?.dataFim,
        top10TamanhoId,
        top10Partes,
      ],
      queryFn: () =>
        producaoService.getRelatorioSabores(
          periodoSomente as RelatorioSaboresProducaoFilters
        ),
      enabled: !!periodoSomente,
    });

  const top10SaboresNoPeriodo = useMemo(
    () => buildTop10SaboresAgregados(linhasPeriodoCompleto),
    [linhasPeriodoCompleto]
  );

  const bottom10SaboresNoPeriodo = useMemo(
    () => buildBottom10SaboresAgregados(linhasPeriodoCompleto),
    [linhasPeriodoCompleto]
  );

  const lojaOptions = lojas.map((l) => ({ value: l.id, label: l.nome }));

  const tamanhoOptions = tamanhos.map((t) => ({
    value: t.id,
    label:
      selectedLojaId != null
        ? t.nome
        : `${t.nome} (${lojas.find((x) => x.id === t.lojaId)?.nome ?? "Loja"})`,
  }));

  const saborOptions = sabores.map((s) => ({ value: s.id, label: s.nome }));

  const usuarioOptions = usuariosLista.map((u) => ({
    value: u.id,
    label: u.nome,
  }));

  const allTamanhoOptions = useMemo(
    () =>
      allTamanhosForLabels.map((t) => ({
        value: t.id,
        label: `${t.nome} (${lojas.find((x) => x.id === t.lojaId)?.nome ?? "Loja"})`,
      })),
    [allTamanhosForLabels, lojas]
  );

  const allUsuarioOptions = useMemo(
    () =>
      (allUsuariosResponse?.data ?? []).map((u) => ({
        value: u.id,
        label: u.nome,
      })),
    [allUsuariosResponse]
  );

  const totalQuantidade = useMemo(
    () => linhas.reduce((acc, r) => acc + r.quantidade, 0),
    [linhas]
  );

  const handleApplyFilter = () => {
    if (warnIfReportPeriodInvalid(startDate, endDate)) return;

    setAppliedFilters({
      dataInicio: startDate,
      dataFim: endDate,
      lojaId: selectedLojaId,
      tamanhoId: selectedTamanhoId,
      saborIds: selectedSaborIds.length > 0 ? selectedSaborIds : undefined,
      usuarioId: selectedUsuarioId,
    });
  };

  const handleClearOptionalFilters = () => {
    setSelectedLojaId(null);
    setSelectedTamanhoId(null);
    setSelectedSaborIds([]);
    setSelectedUsuarioId(null);
  };

  return (
    <RelatorioSaboresContext.Provider
      value={{
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        selectedLojaId,
        setSelectedLojaId,
        selectedTamanhoId,
        setSelectedTamanhoId,
        selectedSaborIds,
        setSelectedSaborIds,
        selectedUsuarioId,
        setSelectedUsuarioId,
        handleApplyFilter,
        handleClearOptionalFilters,
        lojaOptions,
        tamanhoOptions,
        saborOptions,
        usuarioOptions,
        allTamanhoOptions,
        allUsuarioOptions,
        linhas,
        isLoading,
        queryErrorMessage,
        totalQuantidade,
        appliedFilters,
        top10SaboresNoPeriodo,
        bottom10SaboresNoPeriodo,
        isLoadingTop10Periodo,
        top10TamanhoId,
        setTop10TamanhoId,
        top10Partes,
        setTop10Partes,
      }}
    >
      {children}
    </RelatorioSaboresContext.Provider>
  );
};

export const useRelatorioSabores = () => {
  const ctx = useContext(RelatorioSaboresContext);
  if (ctx == null) {
    throw new Error(
      "useRelatorioSabores deve ser usado dentro de RelatorioSaboresProvider"
    );
  }
  return ctx;
};
